// =====================================================================================
// Phaser Synchronization Utilities
// =====================================================================================
// Pure functions for synchronizing React state with Phaser game objects
// Extracted from useRoomConnection to reduce complexity and improve reusability

import type { HomeZone } from 'minka-shared';
import type { HomeZoneClaimState } from '@/types/room.types';
import type { PlayerData } from '../game/types';

/**
 * Private area data structure
 */
interface PrivateAreaData {
  areaId: string;
  name?: string;
  isDoorOpen?: boolean;
  occupants?: Array<{
    userId: string;
    username: string;
  }>;
}

// =====================================================================================
// ZONE DATA ENRICHMENT
// =====================================================================================

/**
 * Enriches zone data with owner information from players list
 * NOTE: As of the backend migration, zones should already be enriched.
 * This function now acts as a fallback for backward compatibility.
 * @param zones - Zone data from WebSocket (may already be enriched)
 * @param players - Current players in the room
 * @param currentUserId - ID of the local user
 * @returns Enriched zones with owner avatar, status, and connection info
 */
export function enrichZoneData(
  zones: HomeZone[], 
  players: PlayerData[], 
  currentUserId?: string
): HomeZone[] {
  return zones.map(zone => {
    // Check if zone is already enriched from backend
    if (zone.claimedByStatus !== undefined && zone.claimedByIsConnected !== undefined) {
      // Zone is already enriched, just ensure local player connection status is correct
      if (zone.claimedBy === currentUserId) {
        return {
          ...zone,
          claimedByIsConnected: true
        };
      }
      return zone;
    }
    
    // Fallback: enrich zone data if not already enriched (backward compatibility)
    if (zone.claimedBy) {
      const owner = players.find(p => p.userId === zone.claimedBy);
      const isLocalPlayerZone = zone.claimedBy === currentUserId;
      
      if (owner) {
        return {
          ...zone,
          claimedByAvatar: zone.claimedByAvatar || owner.avatar,
          claimedByStatus: zone.claimedByStatus || owner.status,
          claimedByIsConnected: isLocalPlayerZone ? true : (zone.claimedByIsConnected ?? true)
        };
      } else if (isLocalPlayerZone) {
        // Local player owns this zone but isn't in players list yet
        return {
          ...zone,
          claimedByIsConnected: true
        };
      }
    }
    return zone;
  });
}

// =====================================================================================
// PLAYER DATA ENRICHMENT  
// =====================================================================================

/**
 * Enriches player data with local player flag
 * @param players - Raw player data from WebSocket
 * @param currentUserId - ID of the local user
 * @returns Players with isLocal flag added
 */
export function enrichPlayerData(players: PlayerData[], currentUserId?: string): PlayerData[] {
  return players.map(player => ({
    ...player,
    isLocal: player.userId === currentUserId
  }));
}

// =====================================================================================
// PHASER GAME OBJECT ACCESS
// =====================================================================================

/**
 * Safely access the Phaser game instance from window global
 * @returns Phaser game instance or null if not available
 */
export function getPhaserGame(): any | null {
  return (window as any).phaserGame || null;
}

/**
 * Check if Phaser game is ready for operations
 * @returns True if game is ready, false otherwise
 */
export function isPhaserGameReady(): boolean {
  return !!(window as any).phaserGame && !!(window as any).phaserGameReady;
}

/**
 * Safely access the main game scene
 * @returns GameScene instance or null if not available
 */
export function getGameScene(): any | null {
  const game = getPhaserGame();
  return game?.scene.getScene('scene-game') || null;
}

// =====================================================================================
// PHASER UPDATE OPERATIONS
// =====================================================================================

/**
 * Update zone data in Phaser game scene
 * @param zones - Enriched zone claims to display; the scene merges them onto
 *                the geometry it already holds from the tilemap
 * @returns True if update was successful, false if game not ready
 */
export function updatePhaserZones(zones: HomeZoneClaimState[]): boolean {
  const gameScene = getGameScene();
  if (!gameScene?.updateZoneData) {
    return false;
  }
  
  gameScene.updateZoneData(zones);
  return true;
}

/**
 * Update private areas in Phaser game scene
 * @param privateAreas - Private area data to display
 * @returns True if update was successful, false if game not ready
 */
export function updatePhaserPrivateAreas(privateAreas: PrivateAreaData[]): boolean {
  const gameScene = getGameScene();
  if (!gameScene?.updatePrivateAreas) {
    return false;
  }
  
  gameScene.updatePrivateAreas(privateAreas);
  return true;
}

/**
 * Refresh a specific player's name tag in Phaser
 * @param userId - ID of the player to update
 * @param status - New status to display
 * @returns True if update was successful, false if game not ready
 */
export function refreshPlayerNameTag(userId: string, status: string): boolean {
  const gameScene = getGameScene();
  if (!gameScene?.refreshPlayerNameTag) {
    return false;
  }
  
  gameScene.refreshPlayerNameTag(userId, status);
  return true;
}

/**
 * Handle private area update in Phaser game
 * @param updateData - Private area update data
 * @returns True if update was successful, false if game not ready
 */
export function handlePhaserPrivateAreaUpdate(updateData: any): boolean {
  const gameScene = getGameScene();
  if (!gameScene?.handlePrivateAreaUpdate) {
    return false;
  }
  
  gameScene.handlePrivateAreaUpdate(updateData);
  return true;
}

/**
 * Handle private area door state update in Phaser game
 * @param areaId - ID of the private area
 * @param isDoorOpen - New door state
 * @returns True if update was successful, false if game not ready
 */
export function handlePhaserDoorUpdate(areaId: string, isDoorOpen: boolean): boolean {
  const gameScene = getGameScene();
  if (!gameScene?.systems?.privateAreas?.handleDoorUpdate) {
    return false;
  }
  
  gameScene.systems.privateAreas.handleDoorUpdate(areaId, isDoorOpen);
  return true;
}

/**
 * Force teleport update in Phaser game (bypasses normal position filtering)
 * @param userId - ID of the player to teleport
 * @param position - New position coordinates
 * @returns True if update was successful, false if game not ready
 */
export function handlePhaserTeleportUpdate(userId: string, position: { x: number; y: number }): boolean {
  const gameScene = getGameScene();
  if (!gameScene?.roomManager?.handleTeleportUpdate) {
    return false;
  }
  
  gameScene.roomManager.handleTeleportUpdate(userId, position);
  return true;
}